// Accounts-list column state. Delegates the visibility/order/widths machinery
// to the generic `useColumnState` hook (src/hooks/useColumnState.ts) and adds
// the accounts-specific view-preset concern on top.

import { useCallback, useEffect, useState } from 'react';
import { useColumnState } from '@/hooks/useColumnState';
import { COLUMNS, type AccountColumnDef } from '../lib/accountsColumnDefs';
import {
  DEFAULT_PRESET,
  ORDER_STORAGE_KEY,
  PRESET_STORAGE_KEY,
  VISIBILITY_STORAGE_KEY,
  WIDTHS_STORAGE_KEY,
  buildPresetVisibility,
  isViewPreset,
  type ViewPreset,
} from '../lib/accountsPresets';

function loadPreset(): ViewPreset {
  try {
    const stored = localStorage.getItem(PRESET_STORAGE_KEY);
    if (isViewPreset(stored)) return stored;
  } catch {
    /* ignore */
  }
  return DEFAULT_PRESET;
}

export interface AccountsColumnState {
  orderedColumns: AccountColumnDef[];
  visibleColumns: AccountColumnDef[];
  columnOrder: string[];
  columnVisibility: Record<string, boolean>;
  columnWidths: Record<string, number>;
  viewPreset: ViewPreset;
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  setColumnOrder: (next: string[]) => void;
  setColumnWidth: (key: string, width: number) => void;
  applyPreset: (preset: ViewPreset) => void;
}

export function useAccountsColumnState(): AccountsColumnState {
  const base = useColumnState<AccountColumnDef>({
    columns: COLUMNS,
    storageKeys: {
      visibility: VISIBILITY_STORAGE_KEY,
      order: ORDER_STORAGE_KEY,
      widths: WIDTHS_STORAGE_KEY,
    },
  });

  const [viewPreset, setViewPresetState] = useState<ViewPreset>(loadPreset);

  useEffect(() => {
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, viewPreset);
    } catch { /* ignore */ }
  }, [viewPreset]);

  const applyPreset = useCallback((preset: ViewPreset) => {
    setViewPresetState(preset);
    base.setColumnVisibility(buildPresetVisibility(preset, COLUMNS));
  }, [base]);

  return {
    orderedColumns: base.orderedColumns,
    visibleColumns: base.visibleColumns,
    columnOrder: base.columnOrder,
    columnVisibility: base.columnVisibility,
    columnWidths: base.columnWidths,
    viewPreset,
    isVisible: base.isVisible,
    toggleColumn: base.toggleColumn,
    setColumnOrder: base.setColumnOrder,
    setColumnWidth: base.setColumnWidth,
    applyPreset,
  };
}
